import React, { FC, ReactNode, useCallback, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { IconProps } from '../../assets/icons/icon.types';
import {
  CapQuestLogo,
  CapTableIcon,
  CircleArrowUpIcon,
  CoinSwapIcon,
  CompaniesIcon,
  DashboardIcon,
  DataRoomIcon,
  DividendsIcon,
  DocumentIcon,
  DoubleChevronIcon,
  EventsIcon,
  FolderIcon,
  HoldingsIcon,
  LogoutIcon,
  OpenBookIcon,
  PoolPlansIcon,
  ProfileIcon,
  ScenarioModelingIcon,
  SettingsIcon,
  ShareClassesIcon,
  StakeholdersIcon,
} from '../../assets/icons/index';
import { sideBarContext } from '../../context/side-bar-context';
import {
  getDocumentSubMenuPaths,
  isModuleLinkVisible,
  roleModuleLinks,
} from '../../guards/page-access.util';
import { Company, User, useScreenSize } from '../../hooks/index';
import { StringKey } from '../../lang/index';
import { CompanySettingsCardTab } from '../../pages/company-settings/company-settings.types';
import { toCompanySettingsPath } from '../../pages/company-settings/company-settings-access.util';
import { usePendingSignaturesCount } from '../../pages/documents/signatures/hooks/use-pending-signatures-count.hook';
import { pageTitleTranslation } from '../../translations/page-translation';
import { PageRoute, TermsRoute } from '../../types/page.types';
import { StakeholderRole } from '../../types/stakeholder.types';
import { AppFormattedMessage } from '../app-formatted-message/index';
import Button from '../button/index';
import CompanySelect from '../company-select/index';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../hover-card/index';
import { Sheet, SheetContent, SheetFooter } from '../sheet/index';
import { CopyCompanyIdItem } from './copy-company-id-item';
import { SideBarItem } from './side-bar-item';

export type SideBarOption = {
  IconComponent: FC<IconProps>;
  link: PageRoute;
  title: StringKey;
};
type SubMenuItem = {
  badgeCount?: number;
  path: PageRoute;
  title: StringKey;
};

const createSubMenu = ({
  isExpanded,
  isMobile,
  items,
  navigate,
  onNavigate,
  pathnameCheck,
  title,
}: {
  isExpanded: boolean;
  isMobile: boolean;
  items: SubMenuItem[];
  navigate: (path: PageRoute) => void;
  onNavigate?: () => void;
  pathnameCheck: (value: string) => boolean;
  title: StringKey;
}): ReactNode => {
  const showHeader = !isExpanded;
  const applyExpandedStyles = isExpanded && isMobile;

  return (
    <div
      className={twMerge(
        'flex flex-col gap-1 text-sm text-gray-700',
        !applyExpandedStyles && 'w-[150px]',
      )}
    >
      {showHeader && (
        <div className="flex w-full cursor-default gap-5 rounded-[4px] p-[6px] text-start text-label-md font-medium text-gray-400">
          <AppFormattedMessage id={title} />
        </div>
      )}

      {items.map(({ badgeCount, path, title }) => (
        <div
          className={twMerge(
            'mb-1 flex w-full cursor-pointer items-center justify-between gap-2 rounded-[4px] p-[6px] text-start hover:bg-[#F4F8FF]',
            applyExpandedStyles && 'ml-1 pl-[46px] text-gray-400 hover:bg-gray-25',
            pathnameCheck(path) && 'bg-[#F4F8FF] text-brand-700',
          )}
          key={path}
          onClick={() => {
            navigate(path);
            onNavigate?.();
          }}
        >
          <AppFormattedMessage id={title} />
          {badgeCount != null && badgeCount > 0 && (
            <span className="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-fireside-500 px-1.5 text-[9px] font-semibold leading-none text-white">
              {badgeCount > 99 ? '99+' : badgeCount}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

const resourcesMenuItems: SubMenuItem[] = [
  { path: PageRoute.PERKS, title: 'PERKS' },
  { path: PageRoute.LIBRARY, title: 'LIBRARY' },
];

const documentSubMenuTitle = (path: PageRoute, role: StakeholderRole | undefined): StringKey => {
  if (path !== PageRoute.DOCUMENTS_SIGNATURES) return 'TEMPLATES';
  return role === StakeholderRole.STAKEHOLDER ? 'FILTER_MY_SIGNATURES' : 'SIGNATURES';
};

const getDocumentsMenu = (
  role: StakeholderRole | undefined,
  signaturesPendingCount: number,
): SubMenuItem[] => {
  const badgeCount = signaturesPendingCount > 0 ? signaturesPendingCount : undefined;
  return getDocumentSubMenuPaths(role).map((path) => ({
    path,
    title: documentSubMenuTitle(path, role),
    badgeCount: path === PageRoute.DOCUMENTS_SIGNATURES ? badgeCount : undefined,
  }));
};

const moduleIconByRoute: Partial<Record<PageRoute, FC<IconProps>>> = {
  [PageRoute.DASHBOARD]: DashboardIcon,
  [PageRoute.STAKEHOLDERS]: StakeholdersIcon,
  [PageRoute.SHARE_CLASSES]: ShareClassesIcon,
  [PageRoute.POOL_PLANS]: PoolPlansIcon,
  [PageRoute.EVENTS]: EventsIcon,
  [PageRoute.SCENARIO_MODELING]: ScenarioModelingIcon,
  [PageRoute.CAP_TABLE]: CapTableIcon,
  [PageRoute.SAFES]: CoinSwapIcon,
  [PageRoute.MY_HOLDINGS]: HoldingsIcon,
  [PageRoute.DIVIDENDS]: DividendsIcon,
  [PageRoute.DOCUMENTS]: DocumentIcon,
  [PageRoute.DATA_ROOM]: DataRoomIcon,
  [PageRoute.VAULT]: FolderIcon,
};

const toSideBarOption = (link: PageRoute): SideBarOption => ({
  link,
  title: pageTitleTranslation[link],
  IconComponent: moduleIconByRoute[link] ?? DocumentIcon,
});

export const sideBarOptions: Record<StakeholderRole, SideBarOption[]> = {
  [StakeholderRole.ADMIN]: roleModuleLinks[StakeholderRole.ADMIN].map(toSideBarOption),
  [StakeholderRole.CONTRIBUTOR]: roleModuleLinks[StakeholderRole.CONTRIBUTOR].map(toSideBarOption),
  [StakeholderRole.STAKEHOLDER]: roleModuleLinks[StakeholderRole.STAKEHOLDER].map(toSideBarOption),
};

const SideBar: FC = () => {
  const navigate = useNavigate();
  const { width: screenWidth } = useScreenSize();
  const { selectedCompany } = Company.useSelected();
  const { companies } = Company.useCompanies({ filter: 'all' });
  const hasCompanies = companies.length > 0;
  const { logoutUser } = User.useLogout();

  const { closeSideBar, isOpen: isExpanded, openSideBar } = useContext(sideBarContext);

  const location = useLocation();
  const isMobile = screenWidth <= 1023;

  const handleLogout = useCallback(() => {
    logoutUser();
  }, [logoutUser]);

  const landingBaseUrl = process.env.REACT_APP_LANDING_URL;
  const isDocumentsSelected =
    location.pathname.startsWith(PageRoute.DOCUMENTS) ||
    location.pathname.startsWith(PageRoute.DOCUMENTS_TEMPLATES) ||
    location.pathname.startsWith(PageRoute.DOCUMENTS_SIGNATURES);

  const documentsPendingCountEnabled = Boolean(
    selectedCompany && isModuleLinkVisible(PageRoute.DOCUMENTS, selectedCompany),
  );
  const pendingSignaturesCount = usePendingSignaturesCount({
    enabled: documentsPendingCountEnabled,
  });

  return (
    <>
      <div className={twMerge('bg-gray-50 px-4 py-3 max-lg:hidden')}>
        <div className={twMerge('flex h-full flex-col justify-between')}>
          <div
            className={twMerge(
              'flex min-h-0 w-10 flex-col justify-stretch transition-all duration-300',
              isExpanded && 'w-[200px]',
            )}
          >
            <Button
              className={twMerge(
                'h-[60px] w-fit shrink-0 items-start bg-gray-50',
                isExpanded ? '-ml-1' : 'ml-1',
              )}
              onClick={() => navigate(PageRoute.HOME)}
              styleType="NONE"
            >
              <CapQuestLogo
                className={isExpanded ? 'ml-2 mt-1' : 'mt-1'}
                mode={isExpanded ? 'full' : 'short'}
                size={isExpanded ? undefined : 31}
              />
            </Button>
            <div className="invisible-y-scroll min-h-0 pb-10">
              {selectedCompany ? (
                <div className={twMerge('flex w-full flex-col gap-3')}>
                  {sideBarOptions[selectedCompany.roleInCompany].map((props, i) => {
                    if (!isModuleLinkVisible(props.link, selectedCompany)) {
                      return null;
                    }

                    if (props.link === PageRoute.DOCUMENTS) {
                      return (
                        <SideBarItem
                          dropdownEnabled={isMobile}
                          Icon={props.IconComponent}
                          iconBadgeDot={pendingSignaturesCount > 0}
                          isExpanded={isExpanded}
                          isSelected={isDocumentsSelected}
                          key={`${props.title}_${i}`}
                          subContent={createSubMenu({
                            isExpanded,
                            isMobile,
                            items: getDocumentsMenu(
                              selectedCompany.roleInCompany,
                              pendingSignaturesCount,
                            ),
                            navigate,
                            onNavigate: isMobile ? closeSideBar : undefined,
                            pathnameCheck: location.pathname.includes.bind(location.pathname),
                            title: 'DOCUMENTS',
                          })}
                          title={props.title}
                        />
                      );
                    }

                    return (
                      <HoverCard key={`${props.title}_${i}`}>
                        <HoverCardTrigger>
                          <SideBarItem
                            Icon={props.IconComponent}
                            isExpanded={isExpanded}
                            isSelected={location.pathname.includes(props.link)}
                            onClick={() => {
                              navigate(props.link);
                            }}
                            {...props}
                          />
                        </HoverCardTrigger>
                        {!isExpanded && (
                          <HoverCardContent
                            className="z-20 w-max rounded-lg bg-[#101828] px-3 py-2"
                            isShownTriangle
                            side="right"
                            sideOffset={8}
                            triangleClassName="left-0 top-1/2 transform -translate-y-1/2 "
                          >
                            <span className="text-label-md font-[550] text-white">
                              <AppFormattedMessage id={props.title} />
                            </span>
                          </HoverCardContent>
                        )}
                      </HoverCard>
                    );
                  })}

                  <div className="h-[1px] w-full bg-gray-200" />
                  <HoverCard>
                    <HoverCardTrigger>
                      <SideBarItem
                        Icon={CompaniesIcon}
                        isExpanded={isExpanded}
                        isSelected={location.pathname === PageRoute.MY_COMPANIES}
                        onClick={() => {
                          navigate(PageRoute.MY_COMPANIES);
                        }}
                        title="MY_COMPANIES"
                      />
                    </HoverCardTrigger>
                    {!isExpanded && (
                      <HoverCardContent
                        className="z-20 w-max rounded-lg bg-[#101828] px-3 py-2"
                        isShownTriangle
                        side="right"
                        sideOffset={8}
                        triangleClassName="left-0 top-1/2 transform -translate-y-1/2 "
                      >
                        <span className="text-label-md font-[550] text-white">
                          <AppFormattedMessage id="MY_COMPANIES" />
                        </span>
                      </HoverCardContent>
                    )}
                  </HoverCard>

                  {isModuleLinkVisible(PageRoute.COMPANY_SETTINGS, selectedCompany) && (
                    <HoverCard>
                      <HoverCardTrigger data-permission-required>
                        <SideBarItem
                          Icon={SettingsIcon}
                          isExpanded={isExpanded}
                          isSelected={location.pathname.includes(PageRoute.COMPANY_SETTINGS)}
                          onClick={() => {
                            navigate(PageRoute.COMPANY_SETTINGS);
                          }}
                          title="SETTINGS"
                        />
                      </HoverCardTrigger>
                      {!isExpanded && (
                        <HoverCardContent
                          className="z-20 w-max rounded-lg bg-[#101828] px-3 py-2"
                          isShownTriangle
                          side="right"
                          sideOffset={8}
                          triangleClassName="left-0 top-1/2 transform -translate-y-1/2 "
                        >
                          <span className="text-label-md font-[550] text-white">
                            <AppFormattedMessage id="SETTINGS" />
                          </span>
                        </HoverCardContent>
                      )}
                    </HoverCard>
                  )}

                  <div className="h-[1px] w-full bg-gray-200" />

                  <SideBarItem
                    dropdownEnabled={isMobile}
                    Icon={OpenBookIcon}
                    isExpanded={isExpanded}
                    isSelected={
                      location.pathname.includes(PageRoute.PERKS) ||
                      location.pathname.includes(PageRoute.LIBRARY)
                    }
                    subContent={createSubMenu({
                      isExpanded,
                      isMobile,
                      items: resourcesMenuItems,
                      navigate,
                      pathnameCheck: location.pathname.includes.bind(location.pathname),
                      title: 'RESOURCES',
                    })}
                    title="RESOURCES"
                  />

                  <CopyCompanyIdItem isExpanded={isExpanded} />
                </div>
              ) : (
                <div className={twMerge('flex w-full flex-col gap-3')}>
                  <HoverCard>
                    <HoverCardTrigger>
                      <SideBarItem
                        Icon={CompaniesIcon}
                        isExpanded={isExpanded}
                        isSelected={location.pathname === PageRoute.MY_COMPANIES}
                        onClick={() => {
                          navigate(PageRoute.MY_COMPANIES);
                        }}
                        title="MY_COMPANIES"
                      />
                    </HoverCardTrigger>
                    {!isExpanded && (
                      <HoverCardContent
                        className="z-20 w-max rounded-lg bg-[#101828] px-3 py-2"
                        isShownTriangle
                        side="right"
                        sideOffset={8}
                        triangleClassName="left-0 top-1/2 transform -translate-y-1/2 "
                      >
                        <span className="text-label-md font-[550] text-white">
                          <AppFormattedMessage id="MY_COMPANIES" />
                        </span>
                      </HoverCardContent>
                    )}
                  </HoverCard>

                  <div className="h-[1px] w-full bg-gray-200" />
                  <SideBarItem
                    dropdownEnabled={isMobile}
                    Icon={OpenBookIcon}
                    isExpanded={isExpanded}
                    isSelected={
                      location.pathname.includes(PageRoute.PERKS) ||
                      location.pathname.includes(PageRoute.LIBRARY)
                    }
                    subContent={createSubMenu({
                      isExpanded,
                      isMobile,
                      items: resourcesMenuItems,
                      navigate,
                      pathnameCheck: location.pathname.includes.bind(location.pathname),
                      title: 'RESOURCES',
                    })}
                    title="RESOURCES"
                  />
                </div>
              )}
            </div>
          </div>
          <Button
            className={twMerge(
              'relative flex size-10 shrink-0 items-center justify-start overflow-hidden transition-all duration-300',
              isExpanded && 'w-full',
            )}
            onClick={isExpanded ? closeSideBar : openSideBar}
            styleType="NONE"
          >
            <div className="z-1 flex size-10 shrink-0 items-center justify-center">
              <DoubleChevronIcon
                className={twMerge('transition-all', !isExpanded && 'rotate-180')}
                iconColor={isExpanded ? '#344054' : '#98A2B3'}
              />
            </div>
            <span
              className={twMerge(
                'absolute left-3 z-0 w-0 overflow-hidden text-xs font-[450] text-gray-700',
                isExpanded && 'w-[100px]',
              )}
            >
              <AppFormattedMessage id="COLLAPSE" />
            </span>
          </Button>
        </div>

        {isMobile && (
          <Sheet modal={false} open={isExpanded}>
            <SheetContent className="mt-[64px] flex w-full gap-0 border-transparent bg-transparent p-0 shadow-none">
              <div
                className="flex h-full min-w-full flex-col gap-3 overflow-y-auto rounded-lg bg-gray-50 p-3"
                style={{ height: 'calc(100% - 64px)' }}
              >
                {selectedCompany && selectedCompany.roleInCompany === StakeholderRole.ADMIN && (
                  <div
                    className="flex w-full items-center gap-4 rounded-lg bg-brand-50 px-3 py-2"
                    onClick={() => {
                      closeSideBar();
                      navigate(toCompanySettingsPath(CompanySettingsCardTab.COMPANY_PLAN_BILLING));
                    }}
                  >
                    <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-brand-700">
                      <CircleArrowUpIcon />
                    </div>
                    <span className="text-sm font-[550] text-brand-700">
                      <AppFormattedMessage id="UPGRADE_ACCOUNT" />
                    </span>
                  </div>
                )}

                {selectedCompany ? (
                  <>
                    <div className="flex h-fit w-full flex-col gap-3 rounded-lg bg-white px-3 py-4">
                      <CompanySelect className="h-10 max-w-none" noAdaptive />
                      {sideBarOptions[selectedCompany.roleInCompany].map((props, i) => {
                        if (!isModuleLinkVisible(props.link, selectedCompany)) {
                          return null;
                        }

                        if (props.link === PageRoute.DOCUMENTS) {
                          return (
                            <SideBarItem
                              dropdownEnabled={isMobile}
                              Icon={props.IconComponent}
                              iconBadgeDot={pendingSignaturesCount > 0}
                              isExpanded={isExpanded}
                              isSelected={isDocumentsSelected}
                              key={`${props.title}_${i}`}
                              subContent={createSubMenu({
                                isExpanded,
                                isMobile,
                                items: getDocumentsMenu(
                                  selectedCompany.roleInCompany,
                                  pendingSignaturesCount,
                                ),
                                navigate,
                                onNavigate: isMobile ? closeSideBar : undefined,
                                pathnameCheck: location.pathname.includes.bind(location.pathname),
                                title: 'DOCUMENTS',
                              })}
                              title={props.title}
                            />
                          );
                        }

                        return (
                          <SideBarItem
                            Icon={props.IconComponent}
                            isExpanded={isExpanded}
                            isSelected={location.pathname.includes(props.link)}
                            key={`${props.title}_${i}`}
                            onClick={() => {
                              closeSideBar();
                              navigate(props.link);
                            }}
                            {...props}
                          />
                        );
                      })}

                      <div className="h-[1px] w-full bg-gray-200" />
                      <SideBarItem
                        Icon={CompaniesIcon}
                        isExpanded={isExpanded}
                        isSelected={location.pathname === PageRoute.MY_COMPANIES}
                        onClick={() => {
                          closeSideBar();
                          navigate(PageRoute.MY_COMPANIES);
                        }}
                        title="MY_COMPANIES"
                      />
                      {isModuleLinkVisible(PageRoute.COMPANY_SETTINGS, selectedCompany) && (
                        <SideBarItem
                          data-permission-required
                          Icon={SettingsIcon}
                          isExpanded={isExpanded}
                          isSelected={location.pathname.includes(PageRoute.COMPANY_SETTINGS)}
                          onClick={() => {
                            closeSideBar();
                            navigate(PageRoute.COMPANY_SETTINGS);
                          }}
                          title="SETTINGS"
                        />
                      )}

                      <div className="h-[1px] w-full bg-gray-200" />

                      <SideBarItem
                        dropdownEnabled={isMobile}
                        Icon={OpenBookIcon}
                        isExpanded={isExpanded}
                        isSelected={
                          location.pathname.includes(PageRoute.PERKS) ||
                          location.pathname.includes(PageRoute.LIBRARY)
                        }
                        subContent={createSubMenu({
                          isExpanded,
                          isMobile,
                          items: resourcesMenuItems,
                          navigate,
                          pathnameCheck: location.pathname.includes.bind(location.pathname),
                          title: 'RESOURCES',
                        })}
                        title="RESOURCES"
                      />

                      <CopyCompanyIdItem isExpanded={isExpanded} onCopied={closeSideBar} />
                    </div>
                    <SheetFooter className="flex h-fit w-full flex-col rounded-lg bg-white px-3 py-4">
                      <SideBarItem
                        Icon={ProfileIcon}
                        isExpanded={isExpanded}
                        isSelected={location.pathname.includes(PageRoute.PROFILE)}
                        onClick={() => {
                          closeSideBar();
                          navigate(PageRoute.PROFILE);
                        }}
                        title="MY_PROFILE"
                      />
                      <div className="text-fire h-[1px] w-full bg-gray-200" />
                      <SideBarItem
                        Icon={LogoutIcon}
                        isExpanded={isExpanded}
                        isSelected={false}
                        onClick={() => {
                          closeSideBar();
                          handleLogout();
                        }}
                        specialIconColor="#D92D20"
                        specialTitleColor="text-fireside-600"
                        title="LOG_OUT"
                      />
                      <div className="mt-3 flex items-center justify-center gap-2 px-1 text-label-sm text-gray-500">
                        <a href={`${landingBaseUrl}${TermsRoute.PRIVACY_POLICY}`} target="_blank">
                          <AppFormattedMessage id="PRIVACY_POLICY" />
                        </a>
                        <div className="h-1 w-1 rounded-full bg-[#D9D9D9]" />
                        <a href={`${landingBaseUrl}${TermsRoute.TERMS_OF_SERVICE}`} target="_blank">
                          <AppFormattedMessage id="TERMS_OF_SERVICE" />
                        </a>
                      </div>
                    </SheetFooter>
                  </>
                ) : (
                  <>
                    <div className="flex h-fit w-full flex-col gap-3 rounded-lg bg-white px-3 py-4">
                      {hasCompanies && <CompanySelect className="h-10 max-w-none" noAdaptive />}
                      <SideBarItem
                        Icon={CompaniesIcon}
                        isExpanded={isExpanded}
                        isSelected={location.pathname === PageRoute.MY_COMPANIES}
                        onClick={() => {
                          closeSideBar();
                          navigate(PageRoute.MY_COMPANIES);
                        }}
                        title="MY_COMPANIES"
                      />
                      <div className="h-[1px] w-full bg-gray-200" />

                      <SideBarItem
                        dropdownEnabled={isMobile}
                        Icon={OpenBookIcon}
                        isExpanded={isExpanded}
                        isSelected={
                          location.pathname.includes(PageRoute.PERKS) ||
                          location.pathname.includes(PageRoute.LIBRARY)
                        }
                        subContent={createSubMenu({
                          isExpanded,
                          isMobile,
                          items: resourcesMenuItems,
                          navigate,
                          pathnameCheck: location.pathname.includes.bind(location.pathname),
                          title: 'RESOURCES',
                        })}
                        title="RESOURCES"
                      />
                    </div>
                    <SheetFooter className="flex h-fit w-full flex-col rounded-lg bg-white px-3 py-4">
                      <SideBarItem
                        Icon={ProfileIcon}
                        isExpanded={isExpanded}
                        isSelected={location.pathname.includes(PageRoute.PROFILE)}
                        onClick={() => {
                          closeSideBar();
                          navigate(PageRoute.PROFILE);
                        }}
                        title="MY_PROFILE"
                      />
                      <div className="text-fire h-[1px] w-full bg-gray-200" />
                      <SideBarItem
                        Icon={LogoutIcon}
                        isExpanded={isExpanded}
                        isSelected={false}
                        onClick={() => {
                          closeSideBar();
                          handleLogout();
                        }}
                        specialIconColor="#D92D20"
                        specialTitleColor="text-fireside-600"
                        title="LOG_OUT"
                      />
                      <div className="mt-3 flex items-center justify-center gap-2 px-1 text-label-sm text-gray-500">
                        <a href={`${landingBaseUrl}${TermsRoute.PRIVACY_POLICY}`} target="_blank">
                          <AppFormattedMessage id="PRIVACY_POLICY" />
                        </a>
                        <div className="h-1 w-1 rounded-full bg-[#D9D9D9]" />
                        <a href={`${landingBaseUrl}${TermsRoute.TERMS_OF_SERVICE}`} target="_blank">
                          <AppFormattedMessage id="TERMS_OF_SERVICE" />
                        </a>
                      </div>
                    </SheetFooter>
                  </>
                )}
              </div>
              <div className="h-full w-full shrink-0" onClick={closeSideBar} />
            </SheetContent>
          </Sheet>
        )}
      </div>
    </>
  );
};

SideBar.displayName = 'SideBar';

export default SideBar;
